import { useEffect, useState } from 'react';

const pageIsVisible = (): boolean =>
  typeof document === 'undefined' || document.visibilityState !== 'hidden';

/**
 * MUS-651: whether a screen has been stuck for `timeoutMs`: `stuck` has held,
 * without a break, for that long while the page was on screen.
 *
 * The clock restarts whenever `stuck` turns true again, `attempt` changes (a
 * retry, or a new play session), or the page comes back from the background,
 * so time spent hidden (a backgrounded app, a locked phone) never counts. The
 * answer drops back to false the moment `stuck` does: a late load clears it.
 */
export const useStalledFor = (stuck: boolean, timeoutMs: number, attempt: string): boolean => {
  if (!Number.isFinite(timeoutMs) || timeoutMs < 0) {
    throw new TypeError(`useStalledFor: timeoutMs must be a non-negative number, got ${timeoutMs}`);
  }
  const [stalled, setStalled] = useState(false);
  const [visibleSince, setVisibleSince] = useState(0);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (pageIsVisible()) setVisibleSince((count) => count + 1);
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  useEffect(() => {
    setStalled(false);
    if (!stuck) return;
    const timer = setTimeout(() => {
      if (pageIsVisible()) setStalled(true);
    }, timeoutMs);
    return () => clearTimeout(timer);
  }, [stuck, timeoutMs, attempt, visibleSince]);

  return stuck && stalled;
};
